<template>
  <v-dialog
    :content-class="'risk-score-popup-content'"
    :max-width='950'
    :value="visible"
    @click:outside='onCloseClickButton'
    @keydown.esc="onCloseClickButton"
  >
    <div class='risk-score-popup'>
      <div class='risk-score-popup__action'>
        <span class='close_btn' @click='onCloseClickButton'>
          <span class="material-icons-outlined">
            close
          </span>
        </span>
      </div>
      <div class='risk-score-popup__header' :class='{"recalculate-clicked": recalculateClicked}'>
        <span class='header-label'>
          {{ $t('risk.score') }}
        </span>
        <div v-if='recalculateRequired' class='risk-score-popup__header__recalculate'>
          <div v-if='!recalculateClicked' class='risk-score-popup__header__recalculate__btn'>
            <animation-tooltip-wrapper
              animation-class-name='recalculate-animation'
              btn-class-name='recalculate-animation'
              refName='recalculateAnimation'
              :animationFile='require(`@/assets/icons/animations/recalculate.json`)'
              :tooltip-text='$t("recalculate.tooltip").toString()'
              @clicked='recalculateRiskScore'
              :disabled='recalculateDisabled'
            />
          </div>
          <div v-else-if='bulk' class='risk-score-popup__header__recalculate__message'>
            <v-alert
              text
              dense
              color="#2459d5"
              icon="info"
              border="left"
              class='not-valid-message'
            >
              {{$t('risk.score.recalculation.initiated')}}
            </v-alert>
          </div>
        </div>
      </div>
      <div class="risk-score-popup__result">
        <div v-if='!bulk && loopActive' class='risk-score-popup__result__loading'>
          <div class='popup-icon'>
            <lottie-animation
              class='recalculating-animation'
              ref="recalculatingAnimation"
              :loop='true'
              :animationData="require(`@/assets/icons/animations/loading.json`)"
            />
          </div>
          <div class='risk-score-popup__result__loading__message'>
            <div class='message'>{{ $t('risk.score.recalculating') }}</div>
          </div>
        </div>
        <div v-else class='risk-score-popup__result__score'>
          <risk-score-percentage
            :color='score.color'
            :text='score.text'
            :score='fixedValue'
            :optimized='optimized'
          />
        </div>
        <div class="risk-score-popup__info">
          <a href='https://kb.cynergy.app/space/GS/902824041' target='_blank'>
            <v-icon class='risk-score-popup__info__icon material-icons-outlined'>info</v-icon>
          </a>
        </div>
        <v-divider class='risk-score-popup__result__divider'
                   inset
                   vertical
        />
        <div class='domains_section'>
          <div class='label'>{{ $t('risk.score.selected.domains') }}</div>
          <div class='domains'>{{ domains.join(', ') }}</div>
        </div>
      </div>
      <div class='no-data' v-if='noData'>
        <div class='no-data__title'>
          {{ $t('risk.score.no.results.found.title') }}
        </div>
        <div class='no-data__subtitle'>
          {{ $t('risk.score.no.results.found.subtitle') }}
        </div>
        <div class='no-data__initiate-test'>
          <v-btn class="initiate-scan-btn" @click="initiateDiscoveryTest" :disabled='initiateTestClicked'>
            {{ initiateScanBtnLabel }}
          </v-btn>
          <span class="scan-initiated" v-if='testInitiated'>
            {{ scanInitiated }}
          </span>
        </div>
      </div>
      <div class='risk-score-popup__content' v-else>
        <risk-score-details-table
          :missingData='missingData'
          :series='detailsSeries'
          :series-max='[5,10,5,10,7,15,13,15,20]'
          :labels='labels'
          :domains='domains'
        />
        <spider-chart
          :series-a='detailsSeries'
          :series-b='industrySeries'
          :series-max='[5,10,5,10,7,15,13,15,20]'
          :labels='labelsFormatted'
          :color-a='score.color'
        />
      </div>
      <div class='risk-score-popup__missing-data-note' v-if='dataMissingExist'>
        {{ $t('risk.score.missing.scans.note') }}
      </div>
    </div>
  </v-dialog>
</template>

<script>
import moment from 'moment';
import SpiderChart from '@/components/charts/SpiderChart.vue';
import RiskScoreDetailsTable from '@/components/risk-score/RiskScoreDetailsTable.vue';
import RiskScorePercentage from '@/components/common/RiskScorePercentage.vue';
import { ADD_NEW_TEST_PROJECT, INITIATE_TEST_CARD } from '@/store/project/actionTypes';
import { TEST_REPEAT_STATUS, TEST_TYPES_TLDS } from '@/constants/projects';
import { getGroupedTimezonesList } from '@/utils/dateUtils';
import disableOuterScrollOnPopupVisible from '@/mixins/disableOuterScrollOnPopupVisible';
import AnimationTooltipWrapper from '@/components/common/AnimationTooltipWrapper.vue';
import LottieAnimation from 'lottie-web-vue';
import { mapGetters } from 'vuex';

const getTimeWithHourOffset = offset => moment()
  .startOf('hour')
  .add(offset, 'hour')
  .format('HH:mm');

const getTimeWithMinuteOffset = offset => moment()
  .startOf('minute')
  .add(offset, 'minute')
  .format('HH:mm');

export default {
  name: 'RiskScorePopup',
  mixins: [disableOuterScrollOnPopupVisible],
  components: {
    LottieAnimation,
    AnimationTooltipWrapper,
    RiskScorePercentage,
    RiskScoreDetailsTable,
    SpiderChart,
  },
  props: {
    recalculateRequired: {
      type: Boolean,
      default: false,
    },
    optimized: {
      type: Boolean,
      default: false,
    },
    missingData: {
      type: Object,
      default: () => ({
        hijack: false,
        smtp: false,
        dns: false,
        cloud: false,
        dataleaks: false,
        employees: false,
        waf: false,
        tech: false,
        ports: false,
      }),
    },
    visible: {
      type: Boolean,
      required: true,
    },
    value: {
      type: Number,
      required: true,
    },
    score: {
      type: Object,
      required: true,
    },
    detailsSeries: {
      type: Array,
      required: true,
    },
    industrySeries: {
      type: Array,
      required: false,
      default: () => [],
    },
    labels: {
      type: Array,
      required: true,
    },
    domains: {
      type: Array,
      required: true,
      default: () => [],
    },
  },
  watch: {
    visible() {
      this.recalculateClicked = false;
      const riskScoreValues = Object.values(this.passiveRiskScore) || [];
      this.bulk = riskScoreValues.filter(riskScoreObj => riskScoreObj.recalculateRequired && this.domains.includes(riskScoreObj.tld)).length > 1;
    },
  },
  computed: {
    ...mapGetters(['recalculationInProgress', 'passiveRiskScore']),
    domainsForRecalculate() {
      const riskScoreValues = Object.values(this.passiveRiskScore) || [];
      return riskScoreValues.filter(riskScoreObj => riskScoreObj.recalculateRequired && this.domains.includes(riskScoreObj.tld)).map(item => item.tld);
    },
    loopActive() {
      return this.recalculationInProgress.some(domain => this.domains.includes(domain));
    },
    recalculateDisabled() {
      return this.recalculateClicked || !this.recalculateRequired;
    },
    dataMissingExist() {
      return Object.keys(this.missingData).some(key => this.missingData[key]) || false;
    },
    formattedTimeZone() {
      return getGroupedTimezonesList().map(tz => ({
        value: tz.offset,
        name: tz.name,
        id: tz.id,
      }));
    },
    fixedValue() {
      return (100 - this.value).toFixed(1);
    },
    noData() {
      return this.detailsSeries.length === 0 || this.detailsSeries.every(item => item === 0);
    },
    initiateScanBtnLabel() {
      return this.$t('configuration.initiate.test');
    },
    scanInitiated() {
      return this.$t('test.initiated.successfully');
    },
    labelsFormatted() {
      const formattedLabels = [];
      this.labels.forEach(label => {
        switch (label.toLowerCase()) {
        case 'hijack':
          formattedLabels.push('hijacking');
          break;
        case 'smtp':
          formattedLabels.push('email');
          break;
        case 'dataleaks':
          formattedLabels.push('data');
          break;
        case 'employees':
          formattedLabels.push('creds');
          break;
        case 'waf':
          formattedLabels.push('appsec');
          break;
        default:
          formattedLabels.push(label);
        }
      });
      return formattedLabels;
    },

  },
  data() {
    return {
      errorMessage: '',
      initiateTestsLoading: false,
      testInitiated: false,
      initiateTestClicked: false,
      recalculateClicked: false,
      bulk: false,
    };
  },
  methods: {
    async recalculateRiskScore() {
      if (!this.recalculateDisabled) {
        this.recalculateClicked = true;
        await this.$emit('recalculate', this.domainsForRecalculate);
      }
    },
    async initiateDiscoveryTest() {
      this.initiateTestClicked = true;
      const selectedTimeZone = this.formattedTimeZone.find(timezone => timezone.value === String(moment().utcOffset()));
      for (let i = 0; i < this.domains.length; i += 1) {
        try {
          const test = {
            url: this.domains[i],
            tld: this.domains[i],
            type: TEST_TYPES_TLDS.discovery,
            schedule: false,
            isAllDay: false,
            startDate: moment().format(),
            startTime: getTimeWithMinuteOffset(2),
            endTime: getTimeWithHourOffset(2),
            timezone: selectedTimeZone.id,
            customRepeatData: {},
            frequency: TEST_REPEAT_STATUS.never,
          };
          // eslint-disable-next-line no-await-in-loop
          const createdTest = await this.$store.dispatch(ADD_NEW_TEST_PROJECT, test);
          if (createdTest) {
            try {
              // eslint-disable-next-line no-await-in-loop
              await this.$store.dispatch(INITIATE_TEST_CARD, [createdTest.id]);
            } catch (e) {
              this.errorMessage = e.message;
            }
          }
        } catch (e) {
          this.errorMessage = e.message;
        }
      }
      this.testInitiated = true;
    },
    onCloseClickButton() {
      this.$emit('closed');
    },
  },
};
</script>

<style lang='scss'>
@import '~@/assets/styles/design-system';
.risk-score-popup {
  .recalculate-animation {
    width: rem(22);
    height: rem(22);
  }
  .recalculating-animation {
    width: rem(46);
    height: rem(46);
  }
  .v-alert {
    margin-top: rem(12);
    font-size: $font-size-sm;
    padding: $space-2;
  }
}
</style>

<style lang='scss' scoped>
@import '~@/assets/styles/design-system';
.risk-score-popup {
  min-height: rem(500);
  &__info {
    margin-top: $space-1-5;
    margin-left: rem(-15);
    margin-right: $space-1-5;
    &__icon {
      color: var(--text-secondary);
      font-size: $space-4;
    }
  }
  &__action {
    .close_btn {
      margin-right: $space-1-5;
      margin-top: rem(10);
      float: right;

      &:hover {
        cursor: pointer;
        color: var(--brand-primary);
      }
    }
  }
  &__header{
    padding-top: rem(35);
    padding-left: rem(35);
    display: flex;
    align-items: center;
    &.recalculate-clicked {
      flex-direction: column;
      align-items: flex-start;
    }
    .header-label{
      color: var(--text-primary);
      font-family: $font-family-bold;
      font-size: rem(24);
      font-weight: bold;
      line-height: $space-5;
      letter-spacing: 0;
    }
    &__recalculate {
      &__btn {
        z-index: 10;
        margin-left: rem(10);
        margin-top: rem(1);
        align-items: center;
        border-radius: $radius-sm;
        display: flex;
        height: rem(26);
        justify-content: center;
        width: rem(26);
        color: #24BEB5 !important;
        background-color: rgba(36, 190, 181, 0.2) !important;
        &:hover {
          cursor: pointer;
          background-color: rgba(36, 190, 181, 0.1) !important;
          border: $border-width-thin solid rgba(36, 190, 181, 0.3);
        }
        &:active{
          background-color: rgba(36, 190, 181, 0.15) !important;
          border: $border-width-thin solid rgba(36, 190, 181, 0.3);
        }
        &.disabled {
          opacity: 0.5;
        }
      }
    }
  }
  &__result{
    display: flex;
    margin-top: rem(15);
    margin-left: rem(35);
    &__score {
      flex-shrink: 0;
      .result{
        font-family: $font-family-black;
        font-size: rem(64);
        text-transform: uppercase;
      }
    }
    &__loading {
      width: rem(195);
      display: flex;
      flex-direction: column;
      z-index: 10;
      align-items: center;
      border-radius: $radius-sm;
      height: rem(64);
      justify-content: center;
      color: var(--text-inverse) !important;
      &:hover {
        border: $border-width-thin solid var(--success-dark);
        background-color: var(--success-hover) !important;
      }
      &__message {
        color: var(--text-secondary);
        font-family: $font-family-regular;
        font-size: $font-size-2xs;
        letter-spacing: 1.1px;
        line-height: rem(15);
        margin-top: $space-2;
        text-transform: uppercase;
        min-width: max-content;
      }
    }
    &__divider{
      margin-left: rem(15);
      max-height: rem(75);
    }
    .domains_section{
      margin-left: rem(15);
      margin-top: rem(10);
      max-height: rem(75);
      max-width: fit-content;
      min-width: 0;
      flex: 1 1 0;
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      .label{
        color: var(--text-secondary);
        font-family: $font-family-regular;
        letter-spacing: 1.1px;
        line-height: rem(15);
        text-transform: uppercase;
        font-size: $font-size-xs;
      }
      .domains{
        color: var(--text-primary);
        font-family: $font-family-medium;
        font-size: rem(15);
        text-transform: uppercase;
        overflow-wrap: break-word;
        overflow: auto;
      }
    }
.value_section{
  padding-left: rem(25);
  display: flex;
  flex-direction: column;
  margin-top: rem(10);
  .value{
    font-family: $font-family-medium;
    font-size: rem(36);
  }
  .label{
    color: var(--text-secondary);
    font-family: $font-family-regular;
    font-size: rem(8);
    letter-spacing: 1.1px;
    line-height: rem(15);
    text-transform: uppercase;
    min-width: max-content;
  }
}
}
.no-data {
text-align: center;
color: var(--text-tertiary);
font-family: $font-family-regular;
font-size: rem(30);
line-height: rem(40);
min-width: rem(650);
display: flex;
justify-content: center;
margin-top: rem(90);
flex-direction: column;
&__subtitle {
  font-family: $font-family-regular;
  font-size: $space-4;
}
&__initiate-test {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  .scan-initiated {
    color: var(--text-primary);
    font-family: $font-family-semibold;
    font-size: $font-size-xs;
    text-transform: uppercase;
  }
  .initiate-scan-btn {
    width: fit-content;
    padding: $space-1 $space-1-5;
    background-color: var(--brand-primary) !important;
    border-radius: $radius-sm;
    box-shadow: $shadow-sm;
    color: #ffffff !important;
    font-family: $font-family-medium;
    font-size: $font-size-xs;
    font-weight: 500;
    letter-spacing: 1.1px;
    line-height: $space-4;
    text-align: center;
    margin-bottom: $space-5;
  }
}
}
&__content{
overflow-y: auto;
min-width: rem(650);
display: flex;
justify-content: center;
margin-top: rem(30);
margin-bottom: rem(10);
}
&__missing-data-note {
margin-bottom: $space-5;
margin-left: $space-5;
font-family: $font-family-regular;
color: var(--severity-critical);
font-size: $font-size-xs;
}
}
</style>
<style lang='scss'>
.v-dialog__content {
  .risk-score-popup-content {
    overflow-y: hidden !important;
  }
}
</style>
